import React, { useRef, useState, useCallback, useEffect } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { duration } from './primitives/tokens';
import styles from './Splitter.module.css';

type SplitterLayout = 'horizontal' | 'vertical';

interface SplitterPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Initial size (px or %) */
  size?: number | string;
  /** Minimum size (px) */
  min?: number;
  /** Maximum size (px) */
  max?: number;
  /** Whether this panel is resizable */
  resizable?: boolean;
  children?: React.ReactNode;
}

const splitter = cva(styles.base, {
  variants: {
    layout: {
      horizontal: styles.horizontal,
      vertical: styles.vertical,
    },
  },
  defaultVariants: { layout: 'horizontal' },
});

interface SplitterProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>, VariantProps<typeof splitter> {
  /** Split direction */
  layout?: SplitterLayout;
  /** Resize handler — receives array of panel sizes in px */
  onResize?: (sizes: number[]) => void;
  children: React.ReactNode;
}

/**
 * Splitter.Panel — individual panel within a Splitter.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | size | number \| string | — | Initial size |
 * | min | number | 0 | Minimum (px) |
 * | max | number | Infinity | Maximum (px) |
 * | resizable | boolean | true | Can be resized |
 */
function SplitterPanel({ children, size: _size, min: _min, max: _max, resizable: _resizable, style, className, ...props }: SplitterPanelProps) {
  return (
    <div className={clsx(styles.panelInner, className)} style={style} {...props}>
      {children}
    </div>
  );
}

/**
 * Splitter — resizable split panes.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | layout | 'horizontal' \| 'vertical' | 'horizontal' | Split direction |
 * | onResize | (sizes: number[]) => void | — | Resize callback |
 *
 * Children must be `Splitter.Panel` components.
 *
 * @example
 * <Splitter>
 *   <Splitter.Panel min={100}><div>Left</div></Splitter.Panel>
 *   <Splitter.Panel><div>Right</div></Splitter.Panel>
 * </Splitter>
 */
export function Splitter({ layout = 'horizontal', onResize, children, className, style, ...props }: SplitterProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const panels = React.Children.toArray(children).filter(
    React.isValidElement,
  ) as React.ReactElement<SplitterPanelProps>[];
  const [sizes, setSizes] = useState<number[]>([]);
  const draggingRef = useRef<number | null>(null);
  const isHorizontal = layout === 'horizontal';
  const panelCount = panels.length;

  // Initialize sizes equally and re-distribute on container resize
  useEffect(() => {
    const el = containerRef.current;
    if (!el || panelCount === 0) return;

    const distribute = () => {
      const total = isHorizontal ? el.offsetWidth : el.offsetHeight;
      const dividers = (panelCount - 1) * 8;
      const available = total - dividers;

      setSizes((prev) => {
        if (prev.length === 0 || prev.length !== panelCount) {
          return Array.from({ length: panelCount }, () => available / panelCount);
        }
        const prevTotal = prev.reduce((a, b) => a + b, 0);
        if (prevTotal === 0) return Array.from({ length: panelCount }, () => available / panelCount);
        return prev.map((s) => (s / prevTotal) * available);
      });
    };

    distribute();
    const ro = new ResizeObserver(distribute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [panelCount, isHorizontal]);

  const handleMouseDown = useCallback(
    (index: number) => (e: React.MouseEvent) => {
      e.preventDefault();
      draggingRef.current = index;

      const startPos = isHorizontal ? e.clientX : e.clientY;
      const startSizes = [...sizes];

      const onMove = (ev: MouseEvent) => {
        if (draggingRef.current === null) return;
        const delta = (isHorizontal ? ev.clientX : ev.clientY) - startPos;
        const next = [...startSizes];
        const i = draggingRef.current;

        const panelA = panels[i];
        const panelB = panels[i + 1];
        const minA = panelA?.props.min ?? 0;
        const maxA = panelA?.props.max ?? Infinity;
        const minB = panelB?.props.min ?? 0;
        const maxB = panelB?.props.max ?? Infinity;

        let newA = startSizes[i] + delta;
        let newB = startSizes[i + 1] - delta;

        newA = Math.min(maxA, Math.max(minA, newA));
        newB = Math.min(maxB, Math.max(minB, newB));

        // Re-balance if clamped
        const totalAB = startSizes[i] + startSizes[i + 1];
        if (newA + newB !== totalAB) {
          if (newA === minA || newA === maxA) newB = totalAB - newA;
          else newA = totalAB - newB;
        }

        next[i] = newA;
        next[i + 1] = newB;
        setSizes(next);
        onResize?.(next);
      };

      const onUp = () => {
        draggingRef.current = null;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [sizes, panels, isHorizontal, onResize],
  );

  return (
    <div
      ref={containerRef}
      className={splitter({ layout, className })}
      style={style}
      {...props}
    >
      {panels.map((panel, i) => (
        <React.Fragment key={i}>
          <div
            className={styles.panel}
            style={isHorizontal
              ? {
                  width: sizes[i] || undefined,
                  minWidth: panel.props.min,
                  maxWidth: panel.props.max,
                  flex: sizes.length === 0 || panelCount === 1 ? 1 : `0 0 ${sizes[i]}px`,
                }
              : {
                  height: sizes[i] || undefined,
                  minHeight: panel.props.min,
                  maxHeight: panel.props.max,
                  flex: sizes.length === 0 || panelCount === 1 ? 1 : `0 0 ${sizes[i]}px`,
                }}
          >
            {panel}
          </div>
          {i < panels.length - 1 && (
            <motion.div
              onMouseDown={handleMouseDown(i)}
              whileHover={{ opacity: 1 }}
              transition={{ duration: duration.normal / 1000 }}
              className={clsx(
                styles.divider,
                isHorizontal ? styles.dividerHorizontal : styles.dividerVertical,
              )}
            >
              <div
                className={clsx(
                  styles.dividerHandle,
                  isHorizontal ? styles.dividerHandleHorizontal : styles.dividerHandleVertical,
                )}
              />
            </motion.div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

Splitter.Panel = SplitterPanel;
